import { BzDateTime } from '@benzinga/date-utils';
import { isNil, formatNumberWithBigInt } from '@benzinga/utils';

import { NO_VALUE } from '../components';

export enum MarketSession {
  afterHours = 'After Hours',
  preMarket = 'Pre Market',
  regular = 'Regular',
  closed = 'Closed',
}

export const renameFoundObjectProperties =
  <Before extends object, After>(keysMap: any) =>
  (original: Before) =>
    (Object.keys(original) as (keyof Before)[]).reduce(
      (acc, beforeKey) => {
        const afterKey = keysMap[beforeKey];
        if (isNil(afterKey)) {
          return acc;
        }
        const originalValue = original[beforeKey];
        if (typeof afterKey === 'string') {
          acc[afterKey] = originalValue;
        } else {
          const newKey = afterKey.after;
          const newValue = afterKey.transform(originalValue);
          acc[newKey] = newValue;
        }
        return acc;
      },
      // tslint:disable-next-line:no-object-literal-type-assertion
      {} as After,
    );

export function formatPhone(phoneNumber: string | number) {
  if (typeof phoneNumber === 'string') {
    const onlyNums = phoneNumber?.replace(/\D/g, '');
    const separatedNums = onlyNums.match(/^(\d{3})(\d{3})(\d{4})$/);
    return separatedNums ? `(${separatedNums[1]}) ${separatedNums[2]}-${separatedNums[3]}` : phoneNumber;
  }
  return phoneNumber;
}

export function numberWithCommas(value: number) {
  return String(value)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

const roundNumber = (value: number, numberOfDecimalPlaces: number) =>
  Math.round(value * Math.pow(10, numberOfDecimalPlaces)) / Math.pow(10, numberOfDecimalPlaces);

// FIXME: not reporting properly?
export function numberShorthand(value: string | number | null | undefined, numberOfDecimalPlaces = 3): string {
  if (isNil(value)) {
    return '';
  }

  const stringValue = typeof value === 'number' ? value.toString() : value;

  if (stringValue.length === 0) {
    return stringValue;
  }

  const regEx = /^([+-]{1})?((?:\d+)?(?:\.\d+)?)$/;
  const matches = regEx.exec(stringValue);

  // Dunno how this should be handled, actually.
  if (matches === null) {
    return stringValue;
  }

  let sign = matches[1];
  let num = Number.parseFloat(matches[2]);

  if (!sign) {
    sign = '';
  }

  if (!num) {
    return '0';
  }

  const numSignificantDigits = String(Math.round(num)).length;
  let signifier = '';
  let divisor = 1;

  if (numSignificantDigits >= 4 && numSignificantDigits < 7) {
    divisor = 1000;
    signifier = 'K';
  } else if (numSignificantDigits >= 7 && numSignificantDigits < 10) {
    divisor = 1000000;
    signifier = 'M';
  } else if (numSignificantDigits >= 10 && numSignificantDigits < 13) {
    divisor = 1000000000;
    signifier = 'B';
  } else if (numSignificantDigits >= 13) {
    divisor = 1000000000000;
    signifier = 'T';
  }

  num /= divisor;

  const roundedNumber = roundNumber(num, numberOfDecimalPlaces);

  return `${sign}${roundedNumber.toFixed(numberOfDecimalPlaces)}${signifier}`;
}

export function numberPercent(value?: string | number | null, isFraction = true) {
  if (isNil(value)) {
    return null;
  }

  if (typeof value === 'number') {
    return `${(value * (isFraction ? 100 : 1)).toFixed(2)}%`;
  }

  if (value.length === 0) {
    return value;
  }

  const num = Number.parseFloat(value);

  if (Number.isNaN(num)) {
    return null;
  }

  const numFraction = num * (isFraction ? 100 : 1);

  return `${numFraction.toFixed(2)}%`;
}

export function numberInMillions(value: number) {
  const shortened = value / 1000000;
  return shortened.toLocaleString();
}

export function numberOfDecimalPlaces(value: number) {
  // fastest method according to jsperf.com
  if (Math.floor(value) === value) {
    return 0;
  }
  return value.toString().split('.')[1].length || 0;
}

export function formatVolume(value: number): string {
  if (value < 1000) {
    return `${value}`;
  } else if (value < 1000000) {
    return `${parseFloat((value / 1000).toFixed(1))}K`;
  } else if (value < 1000000000) {
    return `${parseFloat((value / 1000000).toFixed(1))}M`;
  } else if (Number.isNaN(value)) {
    return NO_VALUE;
  } else {
    return `${parseFloat((value / 1000000000).toFixed(2))}B`;
  }
}

export function formatPrice(input: number) {
  // filters out non-numbers, +/-Infinity, and NaN, undefined, null
  if (!Number.isFinite(input)) {
    return '';
  }

  // 0 returns `0` with no decimal places
  if (input === 0) {
    return '0';
  }

  // return `~0` for numbers near zero
  if (input > -0.0001 && input < 0.0001) {
    return '~0';
  }

  const places = numberOfDecimalPlaces(input);
  if (places > 0) {
    // maximum of 4 decimal places
    if (places > 4) {
      return input.toFixed(4);
    }
    return input.toFixed(places);
  }

  // 0 decimal places, also could use `Number.isInteger(input)`
  if (places === 0) {
    return `${input}.0`;
  }

  // if our tests are correct, this return should be unreachable
  // but just in case
  return input.toString();
}

export function yesOrNo(data: number | string | boolean, fullWord: boolean) {
  if (data === true || data === 'Y' || data === 1) {
    return fullWord ? 'Yes' : 'Y';
  } else if (data === false || data === 'N' || data === 0) {
    return fullWord ? 'No' : 'N';
  }
  return null;
}

export function surprisePercent(estimated: string, actual: string) {
  const estimatedNumber = Number(estimated);
  const actualNumber = Number(actual);

  if (!(Number.isNaN(estimatedNumber) && Number.isNaN(actualNumber)) && estimatedNumber !== 0) {
    return 100 * ((actualNumber - estimatedNumber) / Math.abs(estimatedNumber));
  }

  return '\u00a0';
}

export function roundPercent(value: number) {
  const rounded = Math.round(value * 100) / 100;
  if (rounded) {
    return `${rounded.toLocaleString()}%`;
  }
  return '';
}

export interface CustomTimeLabels {
  afterMarket?: string;
  invalid?: string | null;
  preMarket?: string;
  regular?: string;
}

export function marketTime(time: string, custom: CustomTimeLabels = {}) {
  const preMarket = BzDateTime.fromTime('09:30:00');
  const afterMarket = BzDateTime.fromTime('16:00:00');
  const thisMoment = BzDateTime.fromTime(time);

  if (thisMoment.isBefore(preMarket)) {
    return custom.preMarket || MarketSession.preMarket;
  } else if (thisMoment.equalsOrAfter(afterMarket)) {
    return custom.afterMarket || MarketSession.afterHours;
  } else if (thisMoment.isBefore(afterMarket) && thisMoment.equalsOrAfter(preMarket)) {
    return custom.regular || MarketSession.regular;
  } else if (Object.prototype.hasOwnProperty.call(custom, 'invalid')) {
    return custom.invalid;
  }
  return time;
}

const formatPercentage = (n: number): string => (n === 0 ? '0.00' : n?.toFixed(2));

export const getPerformanceDisplay = (
  label?: string,
  stat?: number | null,
  isPercentage?: boolean,
  suppressPlusSign?: boolean,
  decimalPlaces?: number,
) => {
  let display = '';

  if (isNil(label)) {
    const n = Number(stat);
    if (isPercentage) {
      display = formatPercentage(n);
    } else {
      if (decimalPlaces) {
        display = formatNumberWithBigInt(n, decimalPlaces);
      } else {
        display = `${n}`;
      }
    }

    if (!suppressPlusSign) {
      if (Math.sign(n) === 1) {
        display = `+${display}`;
      }
    }

    if (isPercentage) {
      display = `[${display}%]`;
    }
  } else {
    display = label;
  }

  return display;
};
